import React, { useCallback, useEffect, useState } from "react";
import { CheckSquare, List, Pin, Square, Star, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Button, Card, ChipRow, Input, Overline } from "@/components/app/Primitives";
import { PriorityBadge } from "@/components/app/Badges";
import { CenterSpinner } from "@/components/app/Spinner";

const PRIORITIES = [{ key: "low", label: "Low" }, { key: "medium", label: "Medium" }, { key: "high", label: "High" }, { key: "critical", label: "Critical" }];

export default function Todos() {
  const toast = useToast();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setTodos(await api("/todos")); } catch (e) { console.log("todos", e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const t = await api("/todos", { method: "POST", body: { title: title.trim(), priority } });
      setTodos((p) => [t, ...p]); setTitle(""); toast.show("Added to your to-do", "success");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Could not add", "error"); } finally { setBusy(false); }
  };
  const toggle = async (t) => {
    const next = !t.completed;
    setTodos((p) => p.map((x) => (x.id === t.id ? { ...x, completed: next } : x)));
    try { await api(`/todos/${t.id}`, { method: "PATCH", body: { completed: next } }); }
    catch { setTodos((p) => p.map((x) => (x.id === t.id ? { ...x, completed: t.completed } : x))); }
  };
  const togglePin = async (t) => {
    const next = !t.pinned;
    setTodos((p) => p.map((x) => (x.id === t.id ? { ...x, pinned: next } : x)));
    try { await api(`/todos/${t.id}`, { method: "PATCH", body: { pinned: next } }); load(); } catch { /* noop */ }
  };
  const remove = async (id) => {
    setTodos((p) => p.filter((x) => x.id !== id));
    try { await api(`/todos/${id}`, { method: "DELETE" }); } catch { load(); }
  };

  return (
    <div className="page-in pt-4 md:pt-8" data-testid="todos-page">
      <div className="px-4 pb-2 pt-2"><Overline>Personal</Overline><h1 className="text-[28px] font-extrabold tracking-tight text-a-text">To-Do</h1></div>
      <div className="px-4">
        <Card>
          <div className="space-y-2.5">
            <Input testID="todo-input" placeholder="Add a personal task…" value={title} onChangeText={setTitle} onSubmitEditing={add} />
            <ChipRow options={PRIORITIES} value={priority} onChange={setPriority} testID="todo-priority" />
            <Button testID="todo-add-button" label="Add to my list" onPress={add} loading={busy} />
          </div>
        </Card>
      </div>
      <div className="space-y-2.5 px-4 pt-3">
        {loading && todos.length === 0 ? <CenterSpinner /> : todos.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-2 py-5 text-center">
              <List size={36} className="text-a-muted" />
              <p className="font-bold text-a-text">Your personal list is empty</p>
              <p className="text-a-muted">Capture quick reminders — meetings, calls, ideas.</p>
            </div>
          </Card>
        ) : todos.map((item) => (
          <Card key={item.id} testID={`todo-${item.id}`}>
            <div className="flex items-center gap-2.5">
              <button onClick={() => toggle(item)} data-testid={`todo-toggle-${item.id}`} className={item.completed ? "text-a-success" : "text-a-muted hover:text-a-text"}>
                {item.completed ? <CheckSquare size={24} /> : <Square size={24} />}
              </button>
              <div className="min-w-0 flex-1 space-y-1.5">
                <p className={`line-clamp-2 text-[15px] font-bold text-a-text ${item.completed ? "line-through opacity-55" : ""}`}>{item.title}</p>
                <div className="flex items-center gap-2">
                  <PriorityBadge priority={item.priority} />
                  {item.pinned ? <span className="flex items-center gap-1 text-[11px] font-bold text-a-warning"><Pin size={12} fill="currentColor" />Pinned</span> : null}
                </div>
              </div>
              <button onClick={() => togglePin(item)} data-testid={`todo-pin-${item.id}`} className={item.pinned ? "text-a-warning" : "text-a-muted hover:text-a-warning"}><Star size={20} fill={item.pinned ? "currentColor" : "none"} /></button>
              <button onClick={() => remove(item.id)} data-testid={`todo-delete-${item.id}`} className="text-a-danger hover:opacity-75"><Trash2 size={18} /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
